import { gql, useQuery } from '@apollo/client';
import Button from '@orn/shared-web/components/Button/Button';
import Input from '@orn/shared-web/components/Input';
import { CheckIcon, DollarIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useUpdateUserSettings } from '../../../hooks/mutations/useUpdateUserSettings';

import type { GetLegacySettingsQuery, GetLegacySettingsQueryVariables } from './__generated__/LegacySettingsForm.gql';

// ---------------------------------------------------------------------------
// Base (presentational) — renders the UI from props + callbacks.
// ---------------------------------------------------------------------------

export interface ILegacySettingsFormBaseProps {
  /** Low-balance threshold input value. */
  lowBalanceThreshold: string;
  onLowBalanceThresholdChange: (value: string) => void;
  onLowBalanceThresholdBlur: () => void;
  /** Validation error for the threshold; undefined hides it. */
  thresholdError?: string;
  isSaving: boolean;
  isSaveDisabled: boolean;
  /** Shows the green "Saved" confirmation; holds until the form goes dirty again. */
  justSaved: boolean;
  saveError?: string;
  onSave: () => void;
}

/**
 * Presentational "Legacy" settings form — the low-balance alert threshold plus a
 * right-aligned Save button. A temporary home for pre-redesign settings until
 * each gets its own tab. Fully controlled; the `LegacySettingsForm` wrapper
 * (below) owns the query, state, and the save mutation.
 */
export const LegacySettingsFormBase = ({
  lowBalanceThreshold,
  onLowBalanceThresholdChange,
  onLowBalanceThresholdBlur,
  thresholdError,
  isSaving,
  isSaveDisabled,
  justSaved,
  saveError,
  onSave,
}: ILegacySettingsFormBaseProps) => (
  <div className="flex max-w-[736px] flex-col gap-5">
    <div className="flex flex-col gap-6">
      <div className="text-primary text-lg font-semibold">Alerts</div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-0.5">
          <div className="text-primary text-sm font-medium">Low balance threshold</div>
          <div className="text-tertiary text-sm">Get notified when any account balance falls below this amount.</div>
        </div>
        <Input
          type="number"
          placeholder="500.00"
          icon={DollarIcon}
          value={lowBalanceThreshold}
          onChange={onLowBalanceThresholdChange}
          onBlur={onLowBalanceThresholdBlur}
          isInvalid={Boolean(thresholdError)}
          isRequired
          isDisabled={isSaving}
          hideRequiredIndicator
          hint={thresholdError || undefined}
          size="md"
          className="max-w-sm"
        />
      </div>
    </div>

    <div className="flex flex-col items-end gap-2">
      {saveError && <span className="text-error-primary text-sm">{saveError}</span>}
      {justSaved ? (
        // Stays green until the form goes dirty again (no auto-revert timer).
        <Button color="primary-success" size="md" iconLeading={CheckIcon} className="pointer-events-none">
          Saved
        </Button>
      ) : (
        <Button
          color="primary"
          size="md"
          onClick={onSave}
          isLoading={isSaving}
          isDisabled={isSaveDisabled}
          showTextWhileLoading
        >
          {isSaving ? 'Saving' : 'Save changes'}
        </Button>
      )}
    </div>
  </div>
);

LegacySettingsFormBase.displayName = 'LegacySettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches the threshold + sets up the mutation.
// ---------------------------------------------------------------------------

const GET_LEGACY_SETTINGS = gql`
  query GetLegacySettings {
    getUser {
      id
      settings {
        lowBalanceThreshold
      }
    }
  }
`;

const formatThreshold = (value: number | null | undefined): string => {
  return value != null ? value.toFixed(2) : '';
};

/**
 * "Legacy" settings form wrapper. Fetches the low-balance threshold, owns the
 * input state, and implements the save callback (`updateUserSettings`). Renders
 * `LegacySettingsFormBase` for UI.
 */
const LegacySettingsForm = () => {
  const { data, loading: queryLoading } = useQuery<GetLegacySettingsQuery, GetLegacySettingsQueryVariables>(
    GET_LEGACY_SETTINGS
  );
  const user = data?.getUser;

  const [updateUserSettings, { loading: isSaving }] = useUpdateUserSettings();

  const [lowBalanceThreshold, setLowBalanceThreshold] = React.useState('');
  const [thresholdTouched, setThresholdTouched] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [justSaved, setJustSaved] = React.useState(false);

  React.useEffect(() => {
    if (user) {
      setLowBalanceThreshold(formatThreshold(user.settings.lowBalanceThreshold));
    }
  }, [user]);

  const thresholdValidation = React.useMemo(() => {
    if (!lowBalanceThreshold) {
      if (thresholdTouched) {
        return { isValid: false, errorMessage: 'Low balance threshold is required' };
      }
      return { isValid: false, errorMessage: '' };
    }

    const numValue = parseFloat(lowBalanceThreshold);
    if (isNaN(numValue)) {
      return { isValid: false, errorMessage: 'Please enter a valid number' };
    }
    if (numValue < 0) {
      return { isValid: false, errorMessage: 'Threshold must be a positive number' };
    }
    return { isValid: true, errorMessage: '' };
  }, [lowBalanceThreshold, thresholdTouched]);

  const handleThresholdChange = React.useCallback((value: string) => {
    setThresholdTouched(true);
    setLowBalanceThreshold(value);
  }, []);

  const handleThresholdBlur = React.useCallback(() => {
    if (lowBalanceThreshold) {
      const numValue = parseFloat(lowBalanceThreshold);
      if (!isNaN(numValue)) {
        setLowBalanceThreshold(formatThreshold(numValue));
      }
    }
  }, [lowBalanceThreshold]);

  const thresholdValue = parseFloat(lowBalanceThreshold);
  const hasChanges = !isNaN(thresholdValue) && thresholdValue !== user?.settings.lowBalanceThreshold;

  // Clear the "Saved" confirmation the moment the user edits the threshold again.
  React.useEffect(() => {
    if (hasChanges) {
      setJustSaved(false);
    }
  }, [hasChanges]);

  const handleSave = async () => {
    setThresholdTouched(true);
    setSaveError(null);
    if (!thresholdValidation.isValid) {
      return;
    }
    try {
      await updateUserSettings({ variables: { input: { lowBalanceThreshold: thresholdValue } } });
      setJustSaved(true);
    } catch (error) {
      logger.error('Failed to save alert settings', error);
      setSaveError("Couldn't save your changes. Please try again.");
    }
  };

  if (queryLoading || !user) {
    return <div className="text-secondary">Loading...</div>;
  }

  return (
    <LegacySettingsFormBase
      lowBalanceThreshold={lowBalanceThreshold}
      onLowBalanceThresholdChange={handleThresholdChange}
      onLowBalanceThresholdBlur={handleThresholdBlur}
      thresholdError={thresholdValidation.errorMessage || undefined}
      isSaving={isSaving}
      isSaveDisabled={!hasChanges || !thresholdValidation.isValid || isSaving}
      justSaved={justSaved}
      saveError={saveError ?? undefined}
      onSave={() => void handleSave()}
    />
  );
};

LegacySettingsForm.displayName = 'LegacySettingsForm';

export default LegacySettingsForm;
